import {FieldType} from '../../../models/field-type';
import {FormConfig} from '../../../models/form-config';
import {InsurerDataConsts} from '../../../../core/consts/insurer-data.consts';
import {DictionaryMode, InsurerDataMode, InsurerDataModeConfig, RequiredStrategy} from './insurer-data-mode-config';
import {SelectContentText} from '../../../components/fields/multiselect/material-multiselect.component';
import {BehaviorSubject} from 'rxjs/Rx';
import {FieldConfig} from '../../../models/field-config';
import {EventEmitter} from '@angular/core';
import {Validator} from '../../../models/validator.enum';
import {OwnerDataConsts} from '../../../../core/consts/owner-data.consts';
import {
  DictionaryType,
  DictionaryVersion,
  InsurerDataFormStateService
} from '../../../../core/services/impl/section-services/insurer-data/insurer-data-form-state.service';
import {RegExpKey, RegExpService} from '../../../services/reg-exp.service';

export class InsurerDataSectionConfig {

  private formConfig: FormConfig;
  private cityValidators = [
    Validator.REQUIRED,
    {
      name: Validator.PATTERN,
      pattern: this.regExpService.getRegExp(RegExpKey.CITY)
    },
    {
      name: Validator.MAX_LENGTH,
      maxLength: 100
    },
    {
      name: Validator.MIN_LENGTH,
      minLength: 0
    }];
  private streetValidators: any[] = [
    {
      name: Validator.MAX_LENGTH,
      maxLength: 100
    },
    {
      name: Validator.PATTERN,
      pattern: this.regExpService.getRegExp(RegExpKey.STREET)
    },
    {
      name: Validator.MIN_LENGTH,
      minLength: 2
    }];

  get sectionModeConfig(): InsurerDataModeConfig {
    return this.insurerDataFormStateService.sectionModeConfig;
  }

  get isDictionaryMode(): boolean {
    return this.sectionModeConfig.mode === InsurerDataMode.DICTIONARY;
  }

  get requireStreetInitially(): boolean {
    return this.insurerDataFormStateService.streetRequiredStrategy === RequiredStrategy.ALWAYS_REQUIRED;
  }

  constructor(public insurerDataFormStateService: InsurerDataFormStateService,
              public regExpService: RegExpService) {
  }

  createFormConfig(includeContactDataFields: boolean): FormConfig {
    this.formConfig = {
      formKey: 'INSURER_DATA_SECTION',
      fieldsConfig: [
        {
          fieldKey: InsurerDataConsts.COUNTRY_CODE,
          caption: 'INSURER_DATA_SECTION.FORM_FIELDS.countryCode',
          type: FieldType.Select,
          // validators: [Validator.REQUIRED],
          options: {
            item: 'key',
            label: { label: 'label' },
            items: this.insurerDataFormStateService.countriesDict ? this.insurerDataFormStateService.countriesDict.items.sort((item1, item2) => item1.label.localeCompare(item2.label)) : []
          },
          select: {
            dropdownPosition: 'bottom',
            multiple: false,
            searchable: true,
          }
        },
        {
          fieldKey: InsurerDataConsts.BUILDING_NUMBER,
          caption: 'INSURER_DATA_SECTION.FORM_FIELDS.buildingNumber',
          type: FieldType.TextField,
          validators: [
            Validator.REQUIRED_NOT_BLANK,
            {
              name: Validator.PATTERN,
              pattern: this.regExpService.getRegExp(RegExpKey.BUILDING_NUMBER)
            },
            {
              name: Validator.MAX_LENGTH,
              maxLength: 100
            },
            {
              name: Validator.MIN_LENGTH,
              minLength: 1
            }],
          autocomplete: 'address-line2',
          properties: {
            textTransform: {
              type: 'capitalizeAndRemoveSpaces'
            },
          }
        },
        {
          fieldKey: InsurerDataConsts.FLAT_NUMBER,
          caption: 'INSURER_DATA_SECTION.FORM_FIELDS.flatNumber',
          type: FieldType.TextField,
          validators: [
            {
              name: Validator.PATTERN,
              pattern: this.regExpService.getRegExp(RegExpKey.FLAT_NUMBER)
            },
            {
              name: Validator.MAX_LENGTH,
              maxLength: 100
            },
            {
              name: Validator.MIN_LENGTH,
              minLength: 1
            }],
          autocomplete: 'address-line3',
          properties: {
            textTransform: {
              type: 'capitalizeAndRemoveSpaces'
            },
          }
        },
        {
          fieldKey: InsurerDataConsts.EMAIL,
          caption: 'INSURER_DATA_SECTION.FORM_FIELDS.email',
          type: FieldType.TextField,
          // validators: [Validator.REQUIRED,
          //   {
          //     name: Validator.PATTERN_EMAIL,
          //     patternEmail: '^[a-z|0-9|A-Z]*([_][a-z|0-9|A-Z]+)*([.][a-z|0-9|A-Z]+)*([.][a-z|0-9|A-Z]+)*(([_][a-z|0-9|A-Z]+)*)?@[a-z][a-z|0-9|A-Z]*\.([a-z][a-z|0-9|A-Z]*(\.[a-z][a-z|0-9|A-Z]*)?)$'
          //   },
          //   {
          //     name: Validator.MAX_LENGTH,
          //     maxLength: 250
          //   },
          //   {
          //     name: Validator.MIN_LENGTH,
          //     minLength: 5
          //   }
          // ]
        },
      ]
    };
    this.buildFieldConfigForMode(InsurerDataConsts.CITY);
    this.buildFieldConfigForMode(InsurerDataConsts.STREET);

    if (this.sectionModeConfig.countyVisible) {
      this.addCountyConfig();
    }

    if (includeContactDataFields) {
      this.addContactDataFieldsConfigs();
    }

    return this.formConfig;
  }

  private addCountyConfig(): void {
    this.formConfig.fieldsConfig.push({
      fieldKey: InsurerDataConsts.COUNTY,
      caption: 'INSURER_DATA_SECTION.FORM_FIELDS.county',
      type: FieldType.TextField,
      validators: [
          Validator.REQUIRED_NOT_BLANK,
        {
          name: Validator.PATTERN,
          pattern: this.regExpService.getRegExp(RegExpKey.COUNTY)
        },
        {
          name: Validator.MAX_LENGTH,
          maxLength: 100
        },
        {
          name: Validator.MIN_LENGTH,
          minLength: 5
        }],
    });
  }

  private addContactDataFieldsConfigs(): void {
    const phoneNumberConfig = {
      fieldKey: OwnerDataConsts.PHONE_NUMBER,
      caption: 'OWNER_DATA_SECTION.FORM_FIELDS.phoneNumber',
      personalData: true,
      displayInEditableMode: true,
      type: FieldType.TextField,
      validators: [
        Validator.REQUIRED,
        Validator.PHONE_NUMBER_ZEROES,
        {
          name: Validator.PATTERN,
          pattern: '[0-9]{11}'
        },
        {
          name: Validator.MAX_LENGTH,
          maxLength: 11
        },
        {
          name: Validator.MIN_LENGTH,
          minLength: 2
        }],
      autocomplete: 'tel',
    };

    this.formConfig.fieldsConfig.push(<FieldConfig>phoneNumberConfig);
  }

  private buildFieldConfigForMode(key: string): void {
    let modeSpecificConfig: FieldConfig = {
      fieldKey: key,
      caption: this.getCaption(key),
      type: this.isDictionaryMode ? FieldType.Select : FieldType.TextField,
    };
    if (this.isDictionaryMode) {
      modeSpecificConfig.options = {
        item: this.getPropertyName(key),
        label: {label: this.getPropertyName(key)},
        items: this.getDictionary(key)
      };
      modeSpecificConfig.select = {
        contentTextType: SelectContentText.CAPTION_TEXT,
        dropdownPosition: 'bottom',
        multiple: false,
        loadingEvent: new BehaviorSubject(this.getInitialLoadingValue(key)),
        searchEvent: new EventEmitter(),
        closeEvent: new EventEmitter(),
        searchable: true,
      };
      if (this.sectionModeConfig.dictionaryMode === DictionaryMode.DYNAMIC) {
        modeSpecificConfig.select.dynamicDictionary = true;
        modeSpecificConfig.select.notFoundText = this.insurerDataFormStateService.getAddressItemConfig(key).typeForResultsMessage;
      } else {
        modeSpecificConfig.select.setValueIfOnlyOneItem = true;
      }
    } else { // standard mode
      modeSpecificConfig.autocomplete = this.getAutocomplete(key);
      modeSpecificConfig.properties = {
        textTransform: {
          type: 'capitalize'
        }
      };
    }
    modeSpecificConfig.validators = this.getValidators(key);
    if (key === InsurerDataConsts.CITY) {
      modeSpecificConfig.analyticsConfig = {
        includeFieldValue: true
      }
    }
    this.formConfig.fieldsConfig.push(modeSpecificConfig);
  }

  private getCaption(key: string): string {
    const prefix = 'INSURER_DATA_SECTION.FORM_FIELDS';
    switch (key) {
      case InsurerDataConsts.CITY:
        return `${prefix}.city`;
      case InsurerDataConsts.STREET:
        return `${prefix}.street`;
    }
  }

  private getValidators(key: string): any[] {
    switch (key) {
      case InsurerDataConsts.CITY:
        return this.cityValidators;
      case InsurerDataConsts.STREET:
        if (this.requireStreetInitially) {
          this.streetValidators.push(Validator.REQUIRED);
        }
        return this.streetValidators;
    }
  }

  private getAutocomplete(key: string): string {
    switch (key) {
      case InsurerDataConsts.CITY:
        return 'address-level2';
      case InsurerDataConsts.STREET:
        return 'address-line1';
    }
  }

  private getPropertyName(key: string): string {
    switch (key) {
      case InsurerDataConsts.CITY:
        return 'cityName';
      case InsurerDataConsts.STREET:
        return 'streetName';
    }
  }

  private getDictionary(key: string): any[] {
    switch (key) {
      case InsurerDataConsts.CITY:
        return this.insurerDataFormStateService.getDictionaryForSpecificVersion(DictionaryType.CITIES, { isCoOwner: false, version: DictionaryVersion.INITIAL });
      case InsurerDataConsts.STREET:
        return this.insurerDataFormStateService.getDictionaryForSpecificVersion(DictionaryType.STREETS, { isCoOwner: false, version: DictionaryVersion.INITIAL });
    }
  }

  private getInitialLoadingValue(key: string): boolean {
    switch (key) {
      case InsurerDataConsts.CITY:
        return this.sectionModeConfig.dictionaryMode === DictionaryMode.STATIC;
      case InsurerDataConsts.STREET:
        return false;
    }
}

}
